// -- copyright
// OpenProject is a project management system.
//
// ++

import { ChangeDetectorRef, Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { OpModalComponent } from 'core-components/op-modals/op-modal.component';
import { OpModalLocalsMap } from 'core-components/op-modals/op-modal.types';
import { OpModalLocalsToken } from 'core-components/op-modals/op-modal.service';
import { I18nService } from 'core-app/modules/common/i18n/i18n.service';
import { HalResourceService } from 'core-app/modules/hal/services/hal-resource.service';

declare let layui: any;

export interface SignatureEditModalData {
  siteDiaryId: number;
  projectId: string;
  currentSignatures?: {
    pm_signed_id?: number;
    pm_signed_date?: string;
    contractor_signed_id?: number;
    contractor_signed_date?: string;
    supervisor_signed_id?: number;
    supervisor_signed_date?: string;
  };
  onSuccess?: () => void;
}

@Component({
  templateUrl: './signature-edit-modal.component.html'
})
export class SignatureEditModalComponent extends OpModalComponent {
  // 角色类型
  roleTypes = [
    { key: 'pm', label: 'Site Diary SIOW' },
    { key: 'contractor', label: 'Site Diary Contractor' },
    { key: 'supervisor', label: 'Site Diary Supervisor' }
  ];

  selectedRole = 'pm';
  selectedUserId: number | null = null;
  selectedDate: string | null = null;
  loading = false;
  error: string | null = null;
  users: any[] = [];
  usersLoading = false;

  // 自定义下拉框相关
  showUserDropdown = false;
  filteredUsers: any[] = [];
  selectedUserName = '';
  dropdownMaxHeight = '300px';

  // 从 locals 中获取传入的数据
  private data: SignatureEditModalData;
  private projectId: string;

  @ViewChild('dateInput', { static: true }) dateInput!: ElementRef;

  constructor(
    readonly elementRef: ElementRef,
    @Inject(OpModalLocalsToken) public locals: OpModalLocalsMap,
    readonly cdRef: ChangeDetectorRef,
    readonly I18n: I18nService,
    readonly halResourceService: HalResourceService
  ) {
    super(locals, cdRef, elementRef);
    this.data = locals as any;

    // 获取项目 ID
    const currentProject: any = document.querySelector('meta[name=current_project]');
    this.projectId = currentProject.dataset.projectId;

    // 初始化为当前角色的值
    this.initializeValues();
    // 加载用户列表
    this.loadUsers();
    // 添加全局点击监听器
    this.setupClickOutsideListener();
  }

  initializeValues() {
    const sigs = this.data.currentSignatures || {};
    // 使用对象索引访问，通过类型断言
    this.selectedUserId = (sigs as any)[`${this.selectedRole}_signed_id`] || null;
    this.selectedDate = (sigs as any)[`${this.selectedRole}_signed_date`] || null;
  }

  ngOnInit() {
    // 调用父类的 ngOnInit
    super.ngOnInit();
  }

  onOpen(modalElement:JQuery) {
    console.log('onOpen called');
    super.onOpen(modalElement);
    // 在 Modal 打开后初始化 layui 日期选择器
    // 使用更长的延迟确保 DOM 完全渲染
    setTimeout(() => {
      console.log('About to init layui date picker');
      this.initLayuiDatePicker();
    }, 500);
  }

  private initLayuiDatePicker() {
    console.log('initLayuiDatePicker called');
    console.log('dateInput:', this.dateInput);
    console.log('dateInput.nativeElement:', this.dateInput && this.dateInput.nativeElement);

    if (!this.dateInput || !this.dateInput.nativeElement) {
      console.error('dateInput or nativeElement is null');
      return;
    }

    if (typeof layui !== 'undefined') {
      console.log('layui is defined, layui.laydate:', layui.laydate);

      // 直接使用 layui.laydate，不需要 layui.use
      if (layui.laydate) {
        const laydate = layui.laydate;
        const elem = jQuery(this.dateInput.nativeElement);

        console.log('Rendering laydate on element:', elem);
        console.log('Selected date value:', this.selectedDate);

        laydate.render({
          elem: elem[0], // 传递 DOM 元素
          type: 'datetime',
          format: 'yyyy-MM-dd HH:mm:ss',
          isInitValue: true,
          value: this.selectedDate || '',
          lang: 'en',
          trigger: 'click',
          done: (value: string) => {
            console.log('Date selected:', value);
            this.selectedDate = value;
            this.cdRef.detectChanges();
          }
        });

        console.log('laydate.render called successfully');
      } else {
        console.error('layui.laydate is not defined');
      }
    } else {
      console.error('layui is not defined');
    }
  }

  onRoleChange(roleKey: string) {
    this.selectedRole = roleKey;
    this.initializeValues();
    // 重新加载该角色的用户列表
    this.loadUsers();
  }

  onUserSelected(user: any) {
    this.selectedUserId = user.id;
  }

  onDateChange(date: string) {
    this.selectedDate = date;
  }

  loadUsers() {
    this.usersLoading = true;

    // 根据当前选择的角色加载对应的用户列表
    let role = '';
    switch(this.selectedRole) {
      case 'pm':
        role = 'Consultant';
        break;
      case 'contractor':
        role = 'Contractor';
        break;
      case 'supervisor':
        role = 'Contractor';
        break;
    }

    console.log('Loading users for role:', role, 'project:', this.projectId);

    // 使用项目相关的用户接口
    this.halResourceService
      .get(`/api/v3/projects/${this.projectId}/available_assignees`, { role: role })
      .subscribe(
        (res: any) => {
          // 过滤出 User 类型的元素
          const elements = (res && res.elements) || [];

          const users = elements.filter((item: any) => {
            return item._type === 'User';
          }).map((el: any) => ({
            id: el.id,
            name: el.name,
            href: el.href
          }));

          this.users = users;
          this.filteredUsers = users;
          this.usersLoading = false;
          // 初始化显示选中的用户名
          this.updateSelectedUserName();
        },
        (error) => {
          console.error('Failed to load users:', error);
          this.users = [];
          this.filteredUsers = [];
          this.usersLoading = false;
        }
      );
  }

  updateSelectedUserName() {
    if (this.selectedUserId) {
      const user = this.users.find(u => u.id === this.selectedUserId);
      this.selectedUserName = user ? user.name : '';
    } else {
      this.selectedUserName = '';
    }
  }

  onUserSearch(event: any) {
    const query = event.target.value.toLowerCase();

    if (!query) {
      this.filteredUsers = this.users;
    } else {
      this.filteredUsers = this.users.filter(user =>
        user.name.toLowerCase().includes(query)
      );
    }
    this.showUserDropdown = true;
  }

  toggleUserDropdown() {
    this.showUserDropdown = !this.showUserDropdown;
    if (this.showUserDropdown) {
      this.filteredUsers = this.users;
    }
  }

  selectUser(user: any) {
    this.selectedUserId = user.id;
    this.selectedUserName = user.name;
    this.showUserDropdown = false;
  }

  setupClickOutsideListener() {
    // 使用 jQuery 监听全局点击事件来关闭下拉框
    jQuery(document).on('click', (e) => {
      // 如果点击的不是自定义下拉框内部，则关闭下拉框
      if (!jQuery(e.target).closest('.custom-user-dropdown').length) {
        this.showUserDropdown = false;
        this.cdRef.detectChanges();
      }
    });
  }

  // 在组件销毁时清理事件监听器
  ngOnDestroy() {
    jQuery(document).off('click');
    super.ngOnDestroy();
  }

  submit() {
    this.loading = true;
    this.error = null;

    const payload: any = {
      site_diary_id: this.data.siteDiaryId
    };

    // 只添加当前选择角色的字段
    if (this.selectedUserId !== null) {
      payload[`${this.selectedRole}_signed_id`] = this.selectedUserId;
    }
    if (this.selectedDate !== null) {
      payload[`${this.selectedRole}_signed_date`] = this.selectedDate;
    }

    const csrfToken = jQuery("meta[name=csrf-token]").attr("content") || '';

    // 使用 fetch API 发送请求
    fetch(`/api/v3/projects/${this.data.projectId}/site_diary/update_signature_fields`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Authentication-Scheme': 'Session',
        'X-CSRF-TOKEN': csrfToken
      },
      body: JSON.stringify(payload)
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(err => {
          throw err;
        });
      }
      return response.json();
    })
    .then(data => {
      this.loading = false;
      // 通知父组件刷新
      if (this.data.onSuccess) {
        this.data.onSuccess();
      }
      this.closeMe();
    })
    .catch(err => {
      this.loading = false;
      // 显示错误信息
      if (err && err.errors) {
        this.error = err.errors;
      } else if (err && err.message) {
        this.error = err.message;
      } else {
        this.error = 'Update failed. Please try again.';
      }
      this.cdRef.detectChanges();
    });
  }

  get currentRoleLabel(): string {
    const role = this.roleTypes.find(r => r.key === this.selectedRole);
    return role ? role.label : '';
  }
}
